/* Shot-harness ledger only. Production never loads this. A failed API
 * request must not fall through here. */
import type { Deal, Job, Seller, State } from './types';
import { party, ZERO_SETTLEMENT } from './types';

export const SEED_VERSION = 4;

const H = 3600_000;
const D = 24 * H;

function seller(
  row: Omit<Seller, keyof typeof ZERO_SETTLEMENT | 'channel'> & Partial<Seller>,
): Seller {
  return { ...ZERO_SETTLEMENT, channel: null, ...row };
}

/** People who appear on the seed shot ledger. Not a live directory. */
export const SELLERS: Seller[] = [
  seller({
    name: 'Artem D.',
    tier: 'pro',
    skill: 'Custom scripts — OBS, Telegram bots',
    bio: 'Automation for streamers and channel owners: OBS overlays, alert logic, Telegram bots with TON payments. 61 deals, zero disputes.',
    rate: 45,
    done: 61,
  }),
  seller({
    name: 'Kira V.',
    tier: 'trusted',
    skill: 'AI content — ad creatives, shorts',
    bio: 'AI-assisted production pipeline: ad statics, promo shorts, thumbnails. Volume work with a human pass on every asset.',
    rate: 30,
    done: 38,
  }),
  seller({
    name: 'Lina P.',
    tier: 'trusted',
    skill: 'Twitch channel manager',
    bio: 'Runs two partnered channels end to end: schedule, clips, highlights, sponsor assets. Your channel becomes a system.',
    rate: 28,
    done: 34,
  }),
  seller({
    name: 'Dima T.',
    tier: 'active',
    skill: 'Video editing — VODs to highlights',
    bio: 'Turns raw VODs into tight highlight reels with hooks, captions and pacing built for YouTube retention.',
    rate: 22,
    done: 17,
  }),
  seller({
    name: 'Nia F.',
    tier: 'active',
    skill: 'Music — intros, alerts, loops',
    bio: 'Stream audio identity: intro tracks, alert packs, ambient loops. Stems and full rights included.',
    rate: 25,
    done: 11,
  }),
  seller({
    name: 'Sana R.',
    tier: 'starter',
    skill: 'Chat admin & moderation',
    bio: 'Telegram and Twitch chat moderation: rules, bots, ban lists, night coverage. Calm chat, no drama.',
    rate: 12,
    done: 3,
  }),
];

export function sellerByName(name: string): Seller | undefined {
  return SELLERS.find((s) => s.name === name);
}

function seedJobs(now: number): Job[] {
  const jobs: Array<Omit<Job, 'proposals' | 'status' | 'mine' | 'depositTx'> & Partial<Job>> = [
    {
      id: '0151',
      title: 'Streamer landing site — link hub + promo pages',
      category: 'Development',
      description:
        'One-page site for a casino streamer: link hub, current bonus table, VOD embeds, RU + EN. Assets and copy are ready. Deploy the static site, hand off the repo.',
      budget: 180,
      milestones: [
        { title: 'Design comp', amount: 45 },
        { title: 'Build + CMS', amount: 90 },
        { title: 'Launch + handoff', amount: 45 },
      ],
      client: party('DegenMax', 'trusted'),
      postedAt: now - 2 * H,
      proposalCount: 6,
      autoRelease: true,
    },
    {
      id: '0150',
      title: 'Ad pack for channel promo — 10 statics + 3 shorts',
      category: 'Content',
      description:
        'Telegram channel promo: 10 static creatives in 3 sizes and 3 vertical shorts under 20s. Brand kit provided. AI tools fine — every asset gets reviewed before release.',
      budget: 90,
      milestones: [
        { title: '10 statics', amount: 50 },
        { title: '3 shorts', amount: 40 },
      ],
      client: party('NightRush', 'trusted'),
      postedAt: now - 5 * H,
      proposalCount: 9,
      autoRelease: true,
    },
    {
      id: '0149',
      title: 'Stream intro track + 8 alert sounds',
      category: 'Music',
      description:
        'Original 45–60s intro track plus a pack of 8 alert sounds (follow, sub, raid, donation tiers). Full rights transfer, stems included. Reference playlist in the brief.',
      budget: 70,
      milestones: [
        { title: 'Intro track', amount: 40 },
        { title: 'Alert pack', amount: 30 },
      ],
      client: party('Marta K.', 'active'),
      postedAt: now - 26 * H,
      proposalCount: 4,
      autoRelease: true,
    },
    {
      id: '0148',
      title: 'Cut 8 VODs into YouTube highlights',
      category: 'Video',
      description:
        'Eight 3–5h VODs into 10–12 min highlight reels: hooks in the first 15s, captions, chapter marks. Raw files via Telegram, one revision per video.',
      budget: 120,
      milestones: [
        { title: 'First 4 edits', amount: 60 },
        { title: 'Final 4 edits', amount: 60 },
      ],
      client: party('Oleg V.', 'active'),
      postedAt: now - 30 * H,
      proposalCount: 7,
      autoRelease: true,
    },
    {
      id: '0147',
      title: 'Telegram bot — VIP chat access + TON payments',
      category: 'Development',
      description:
        'Bot that sells access to a private chat: TON payment in, invite link out, auto-kick on expiry, admin commands. grammY preferred. Spec doc is written.',
      budget: 150,
      milestones: [
        { title: 'Bot core', amount: 70 },
        { title: 'Payments + access', amount: 50 },
        { title: 'Admin commands', amount: 30 },
      ],
      client: party('tonlabs.dev', 'pro'),
      postedAt: now - 2 * D,
      proposalCount: 11,
      autoRelease: true,
    },
  ];

  const mine: Job = {
    id: '0146',
    title: 'Weekly VOD highlights — 4 videos',
    category: 'Video',
    description:
      'Ongoing: every week, 4 VODs cut into 8–10 min highlights with captions and thumbnails. This deal covers the first batch; extend if the cut quality holds.',
    budget: 110,
    milestones: [
      { title: 'First 2 edits', amount: 55 },
      { title: 'Final 2 + thumbnails', amount: 55 },
    ],
    client: party('Yuri M.', 'active'),
    mine: true,
    postedAt: now - 3 * H,
    status: 'open',
    proposalCount: 2,
    autoRelease: true,
    depositTx: null,
    proposals: [
      {
        id: 'p-0146-1',
        from: sellerByName('Lina P.')!,
        price: 100,
        days: 10,
        message:
          'I cut highlights for two partnered channels every week — same format you want. First two edits within 4 days so you can judge the pacing early.',
        status: 'pending',
        sentAt: now - 2 * H,
      },
      {
        id: 'p-0146-2',
        from: sellerByName('Dima T.')!,
        price: 110,
        days: 7,
        message:
          'Retention-first cuts: hook in 15s, captions burned in, chaptered. I batch all 4 in one week and include thumbnail A/B variants.',
        status: 'pending',
        sentAt: now - 1 * H,
      },
    ],
  };

  return [
    mine,
    ...jobs.map((j) => ({
      ...j,
      mine: false,
      status: 'open' as const,
      proposals: [],
      depositTx: null,
    })),
  ];
}

function seedDeal(
  row: Omit<Deal, 'contractAddress' | 'groupChatId' | 'chainRead' | 'fundedAmount'> &
    Partial<Deal>,
): Deal {
  const funded = row.milestones.reduce((s, m) => s + m.amount, 0);
  return {
    contractAddress: row.contract || null,
    groupChatId: null,
    chainRead: row.contract ? 'ok' : 'none',
    fundedAmount: row.contract ? funded : null,
    ...row,
  };
}

function seedDeals(now: number): Deal[] {
  return [
    seedDeal({
      id: '0142',
      title: 'VOD highlights — March batch',
      role: 'client',
      counterparty: party('Lina P.', 'trusted'),
      contract: 'EQD7…4KQC',
      status: 'active',
      createdAt: now - 9 * D,
      milestones: [
        { title: 'First 2 edits', amount: 30, status: 'released', releasedVia: 'manual' },
        {
          title: 'Next 4 edits',
          amount: 55,
          status: 'delivered',
          deliveredAt: now - 1 * H,
          note: 'All four cut and uploaded — links in the deal chat. Ep. 3 ran long, trimmed to 11:40. One revision each still included.',
        },
        { title: 'Final 2 + thumbnails', amount: 40, status: 'locked' },
      ],
      events: [
        { at: now - 9 * D, text: 'ESCROW FUNDED — 125 TON LOCKED' },
        { at: now - 6 * D, text: 'M1 DELIVERED BY LINA P.' },
        { at: now - 5 * D, text: 'M1 RELEASED +30 TON → LINA P.' },
        { at: now - 1 * H, text: 'M2 DELIVERED — 72H CLOCK ARMED' },
      ],
    }),
    seedDeal({
      id: '0139',
      title: 'VIP access bot — NightRush',
      role: 'freelancer',
      counterparty: party('NightRush', 'trusted'),
      contract: 'EQBn…7A2E',
      status: 'active',
      createdAt: now - 12 * D,
      milestones: [
        {
          title: 'Bot core + payments',
          amount: 80,
          status: 'delivered',
          deliveredAt: now - 58 * H,
          note: 'Bot live on @nightrush_vip_bot: TON payment → invite link → auto-kick on expiry. Test invoice of 0.1 TON went through, tx in the chat.',
        },
        { title: 'Admin commands', amount: 70, status: 'locked' },
      ],
      events: [
        { at: now - 12 * D, text: 'ESCROW FUNDED — 150 TON LOCKED' },
        { at: now - 58 * H, text: 'M1 DELIVERED — 72H CLOCK ARMED' },
      ],
    }),
    seedDeal({
      id: '0131',
      title: 'AI ad pack — 20 statics',
      role: 'freelancer',
      counterparty: party('Oleg V.', 'active'),
      contract: 'EQAj…9C51',
      status: 'disputed',
      disputeBy: 'client',
      disputeReason:
        'Client: statics 11–20 reuse layouts from the first batch, brief asked for 4 distinct directions. Freelancer: directions match the moodboard signed off at M1.',
      createdAt: now - 15 * D,
      milestones: [
        { title: 'First 10 statics', amount: 40, status: 'released', releasedVia: 'manual' },
        { title: 'Final 10 statics', amount: 20, status: 'disputed' },
      ],
      events: [
        { at: now - 15 * D, text: 'ESCROW FUNDED — 60 TON LOCKED' },
        { at: now - 8 * D, text: 'M1 RELEASED +40 TON' },
        { at: now - 3 * D, text: 'M2 DELIVERED — 72H CLOCK ARMED' },
        { at: now - 2 * D, text: 'DISPUTE OPENED BY CLIENT — CLOCK FROZEN' },
        { at: now - 2 * D + 2 * H, text: 'ARBITER ASSIGNED — CASE 0131-A' },
      ],
    }),
    seedDeal({
      id: '0118',
      title: 'OBS overlay scripts — Marta K.',
      role: 'freelancer',
      counterparty: party('Marta K.', 'active'),
      contract: 'EQCk…2F19',
      status: 'settled',
      rating: 5,
      createdAt: now - 34 * D,
      milestones: [
        { title: 'Overlay pack + install', amount: 85, status: 'released', releasedVia: 'manual' },
      ],
      events: [
        { at: now - 34 * D, text: 'ESCROW FUNDED — 85 TON LOCKED' },
        { at: now - 29 * D, text: 'M1 DELIVERED' },
        { at: now - 28 * D, text: 'M1 RELEASED +85 TON — DEAL SETTLED' },
      ],
    }),
  ];
}

export function buildSeed(now = Date.now()): State {
  return {
    me: {
      id: 1,
      name: 'Yuri M.',
      handle: '@yuri_builds',
      skill: 'Custom scripts · AI content',
      bio: '',
      rate: 25,
      done: 1,
      channel: null,
      wallet: 'UQBx4k…9kFa',
      staked: 12,
      tier: 'active',
      ...ZERO_SETTLEMENT,
      dealsSettled: 1,
      tonReleased: 85,
      distinctCounterparties: 1,
    },
    jobs: seedJobs(now),
    deals: seedDeals(now),
    toasts: [],
  };
}
